import { createI18n, type I18n, type LocaleMessageValue, type VueMessageType } from 'vue-i18n'
import en from './locales/en.json'
import es from './locales/es.json'
import ptBR from './locales/pt-BR.json'
import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  LOCALE_STORAGE_KEY,
  LOCALES,
  type LocaleDescriptor,
  findLocale,
} from './locales'
import { localeFromPath } from './routing'

export {
  DEFAULT_LOCALE,
  findLocale,
  isSupportedLocale,
  LOCALE_STORAGE_KEY,
  LOCALES,
  type LocaleDescriptor,
}

/** One locale's translations: nested objects bottoming out in format strings. */
export type Catalog = Record<string, LocaleMessageValue<VueMessageType>>

const sharedMessages: Record<string, Catalog> = { en, 'pt-BR': ptBR, es }

/**
 * Which language to show, most explicit signal first:
 *   1. the URL's locale prefix — it is what was shared, linked and crawled
 *   2. the visitor's stored preference
 *   3. the browser's languages
 *   4. English
 *
 * The URL outranks the stored preference on purpose: following a link to
 * `/pt-br/...` must show Portuguese even for someone who once picked Spanish.
 */
export function detectLocale(pathname?: string): string {
  const path = pathname ?? (typeof location === 'undefined' ? '/' : location.pathname)
  const fromUrl = localeFromPath(path)
  if (fromUrl) return fromUrl

  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (isSupportedLocale(stored)) return stored as string
  } catch {
    // ignore unreadable storage
  }

  if (typeof navigator !== 'undefined') {
    for (const candidate of navigator.languages ?? [navigator.language]) {
      if (isSupportedLocale(candidate)) return candidate
      // Fall back from a region variant to any locale sharing its base language.
      const base = candidate.split('-')[0]
      const match = LOCALES.find((locale) => locale.code.split('-')[0] === base)
      if (match) return match.code
    }
  }
  return DEFAULT_LOCALE
}

/**
 * Builds the i18n instance. `toolMessages` are merged over the shared catalog so a
 * tool can add its own keys without touching the shell.
 */
export function createAppI18n(toolMessages: Record<string, Catalog> = {}): I18n {
  const messages: Record<string, Catalog> = {}
  for (const { code } of LOCALES) {
    messages[code] = { ...(sharedMessages[code] ?? {}), ...(toolMessages[code] ?? {}) }
  }

  return createI18n({
    legacy: false,
    globalInjection: true,
    locale: detectLocale(),
    fallbackLocale: DEFAULT_LOCALE,
    // Missing keys fall back to English silently in production, loudly in dev.
    missingWarn: import.meta.env?.DEV ?? false,
    fallbackWarn: import.meta.env?.DEV ?? false,
    messages,
  })
}

/** Persists the choice and updates <html lang>. Never reloads the page. */
export function persistLocale(code: string): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, code)
  } catch {
    // ignore unwritable storage
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = findLocale(code)?.hreflang ?? code
  }
}
